import { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL, API_HEADERS, API_TIMEOUT } from '../config';

export const useApi = <T>(endpoint: string) => {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    const url = `${API_BASE_URL}${endpoint}`;

    try {
      const token = sessionStorage.getItem('corvus_token');
      const headers = {
        ...API_HEADERS,
        ...(token ? { 'Authorization': token } : {})
      };

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);

      const response = await fetch(url, {
        headers,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      // Si la respuesta tiene una propiedad 'data', usar esa
      const finalData = result.data || result;
      
      // Normalizar los datos para asegurar compatibilidad
      const normalizeData = (item: any) => {
        // Si no es un objeto, devolverlo tal cual
        if (!item || typeof item !== 'object') return item;
        
        // Crear una copia para no modificar el original
        const normalized = { ...item };
        
        // Normalizar cargocapacity a cargo_capacity
        if ('cargocapacity' in normalized && !('cargo_capacity' in normalized)) {
          normalized.cargo_capacity = normalized.cargocapacity;
        }
        
        // Asegurarse de que manufacturer sea un objeto si existe
        if (normalized.manufacturer && typeof normalized.manufacturer !== 'object') {
          normalized.manufacturer = {
            name: String(normalized.manufacturer),
            id: 0,
            code: '',
            description: '',
            known_for: '',
            imagen: ''
          };
        }
        
        return normalized;
      };

      // Aplicar normalización a los datos
      let normalizedData;
      if (Array.isArray(finalData)) {
        normalizedData = finalData.map(normalizeData);
      } else {
        normalizedData = normalizeData(finalData);
      }

      setData(normalizedData as T);
      setError(null);
    } catch (error) {
      setError(error as Error);
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refetch = useCallback(() => {
    setLoading(true);
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch };
}; 